/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';

import { ShareType } from '@api/types';

type ShareStoreStateType = {
  data: ShareType;
  initialized: boolean;
};

const initialState = (): ShareStoreStateType => ({
  data: {
    experiencePeriodCategories: [],
    skillStatusTypes: [],
    languages: [],
    languageProficiencies: [],
    subscriptions: [],
    tenantUserRoles: [],
    timezones: [],
  },
  initialized: false,
});

export const useShareStore = defineStore({
  id: 'share',
  state: initialState,
  getters: {
    experiencePeriodCategoryById: (state) => (id: number | null) => {
      if (id === null) return null;
      const index = state.data.experiencePeriodCategories.findIndex(
        (elem) => elem.id === id,
      );
      return index >= 0 ? state.data.experiencePeriodCategories[index] : null;
    },
    skillStatusTypeById: (state) => (id: number | null) => {
      if (id === null) return null;
      const index = state.data.skillStatusTypes.findIndex(
        (elem) => elem.id === id,
      );
      return index >= 0 ? state.data.skillStatusTypes[index] : null;
    },
    tenantRoleByUid: (state) => (uid: number) => {
      const index = state.data.tenantUserRoles.findIndex(
        (elem) => elem.uid === uid,
      );
      return index >= 0 ? state.data.tenantUserRoles[index] : null;
    },
    tenantGeneralRole: (state) => {
      const minAccessLevel = Math.min(
        ...state.data.tenantUserRoles.map((elem) => elem.accessLevel),
      );
      const index = state.data.tenantUserRoles.findIndex(
        (elem) => elem.accessLevel === minAccessLevel,
      );
      return index >= 0 ? state.data.tenantUserRoles[index] : null;
    },
    tenantMemberRole: (state) => {
      const minAccessLevel = Math.min(
        ...state.data.tenantUserRoles.map((elem) => elem.accessLevel),
      );
      const index = state.data.tenantUserRoles.findIndex(
        (elem) => elem.accessLevel === minAccessLevel,
      );
      return index >= 0 ? state.data.tenantUserRoles[index] : null;
    },
    tenantManagerRole: (state) => {
      const maxAccessLevel = Math.max(
        ...state.data.tenantUserRoles.map((elem) => elem.accessLevel),
      );
      const minAccessLevel = Math.min(
        ...state.data.tenantUserRoles.map((elem) => elem.accessLevel),
      );
      const index = state.data.tenantUserRoles.findIndex(
        (elem) => (
          elem.accessLevel !== maxAccessLevel
          && elem.accessLevel !== minAccessLevel
        ),
      );
      return index >= 0 ? state.data.tenantUserRoles[index] : null;
    },
    tenantAdminRole: (state) => {
      const maxAccessLevel = Math.max(
        ...state.data.tenantUserRoles.map((elem) => elem.accessLevel),
      );
      const index = state.data.tenantUserRoles.findIndex(
        (elem) => elem.accessLevel === maxAccessLevel,
      );
      return index >= 0 ? state.data.tenantUserRoles[index] : null;
    },
    enterpriseSubscription: (state) => {
      const index = state.data.subscriptions.findIndex((elem) => elem.isEnterprise);
      return index >= 0 ? state.data.subscriptions[index] : null;
    },
    languageByCode: (state) => (code: string) => {
      const index = state.data.languages.findIndex(
        (elem) => elem.code === code,
      );
      return index >= 0 ? {
        ...state.data.languages[index],
      } : null;
    },
    languageProficiencyByUid: (state) => (uid: number) => {
      const index = state.data.languageProficiencies.findIndex(
        (elem) => elem.uid === uid,
      );
      return index >= 0 ? {
        ...state.data.languageProficiencies[index],
      } : null;
    },
  },
  actions: {
    $reset() {
      Object.assign(this, initialState());
    },
    set(data: ShareType): void {
      Object.keys(this.data).forEach((key) => {
        this.data[key] = data[key];
      });
      this.initialized = true;
    },
  },
});
